// src/pages/FeedbackPage.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaBug, FaLightbulb, FaSave } from "react-icons/fa";

const COLOR = {
  primary: "var(--app-primary)",
  accent: "#22c55e",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const FeedbackPage = () => {
  const [type, setType] = useState("bug");
  const [severity, setSeverity] = useState("medium");
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [contact, setContact] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = () => {
    if (!title.trim() || !details.trim()) {
      toast.info("Please add a title and details");
      return;
    }
    setSaving(true);
    setTimeout(() => {
      toast.success("Thanks! We logged your feedback.");
      setSaving(false);
      setTitle("");
      setDetails("");
      setContact("");
      setType("bug");
      setSeverity("medium");
    }, 600);
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2000} />
      <Top>
        <div>
          <Title>Feedback & Bugs</Title>
          <Sub>
            Report bugs, request features, or share ideas for the restaurant
            app.
          </Sub>
          <BadgeRow>
            <TypeBadge
              type="button"
              $danger={type === "bug"}
              $active={type === "bug"}
              onClick={() => setType("bug")}
            >
              <FaBug />
              Bug
            </TypeBadge>
            <TypeBadge
              type="button"
              $active={type === "feature"}
              onClick={() => setType("feature")}
            >
              <FaLightbulb />
              Feature
            </TypeBadge>
            <TypeBadge
              type="button"
              $active={type === "other"}
              onClick={() => setType("other")}
            >
              ✨ Other
            </TypeBadge>
          </BadgeRow>
        </div>

        <StatRow>
          <StatChip>Avg response: 15 min</StatChip>
          <StatChip>Issues fixed: 120+</StatChip>
        </StatRow>
      </Top>

      <MainLayout>
        <Card>
          <CardHeader>
            <CardTitle>Submit feedback</CardTitle>
            <CardSub>
              Give as much detail as possible. This helps us fix faster.
            </CardSub>
          </CardHeader>

          <FormGrid>
            <Field>
              <Label>Title</Label>
              <Input
                placeholder="Short summary, e.g. 'KOT not printing for table 4'"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>

            <Field>
              <Label>Severity</Label>
              <Select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
              >
                <option value="low">Low - minor issue</option>
                <option value="medium">Medium - affecting some flows</option>
                <option value="high">High - blocking orders/billing</option>
                <option value="critical">Critical - system not usable</option>
              </Select>
            </Field>
          </FormGrid>

          <Field>
            <Label>Details</Label>
            <Textarea
              rows={6}
              placeholder="Steps to reproduce, expected vs actual result, device (Android/iOS/Web), printer model, etc."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />
            <Helper>
              Example: Open Orders &gt; select table 4 &gt; tap Print KOT &gt;
              error "Printer not found".
            </Helper>
          </Field>

          <Field>
            <Label>
              Contact
              <OptionalTag>Optional</OptionalTag>
            </Label>
            <Input
              placeholder="Email or phone to reach you"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
          </Field>

          <Actions>
            <TypeChip>
              Type:{" "}
              <strong>
                {type === "bug"
                  ? "Bug report"
                  : type === "feature"
                  ? "Feature request"
                  : "Other feedback"}
              </strong>
            </TypeChip>
            <PrimaryBtn onClick={submit} disabled={saving}>
              <FaSave />
              {saving ? "Saving..." : "Submit feedback"}
            </PrimaryBtn>
          </Actions>
        </Card>

        <SidePanel>
          <SideTitle>Tips for better feedback</SideTitle>
          <ul>
            <li>Mention if you are using Android, iOS, or Web.</li>
            <li>
              For printer issues, add printer model and connection type (USB,
              Bluetooth, Wi-Fi).
            </li>
            <li>Share when the issue started and how often it happens.</li>
          </ul>

          <SideDivider />

          <SideTitle>Attach screenshots from mobile</SideTitle>
          <SideText>
            When you send feedback from the mobile app, attach images if
            possible. It helps reproduce and fix issues faster.
          </SideText>
        </SidePanel>
      </MainLayout>
    </Page>
  );
};

export default FeedbackPage;

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: auto;
  padding: 10px 12px;
  display: grid;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
`;

const Top = styled.section`
  background: ${COLOR.card};
  border-radius: 14px;
  padding: 12px;
  border: 1px solid ${COLOR.line};
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;

const Sub = styled.p`
  margin: 4px 0 0;
  color: ${COLOR.muted};
  max-width: 700px;
  font-size: 0.9rem;
`;

const BadgeRow = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const TypeBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid
    ${({ $danger, $active }) =>
      $danger
        ? "rgba(248, 113, 113, 0.9)"
        : $active
        ? "rgba(52, 211, 153, 0.9)"
        : "rgba(148, 163, 184, 0.9)"};
  background: ${({ $danger, $active }) =>
    $danger
      ? "rgba(254, 226, 226, 0.8)"
      : $active
      ? "rgba(224, 242, 254, 0.9)"
      : "rgba(255, 255, 255, 0.9)"};
  color: ${({ $danger, $active }) =>
    $danger ? "#991b1b" : $active ? "#065f46" : COLOR.text};
  cursor: pointer;
  font-weight: 800;
  font-size: 0.8rem;
`;

const StatRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-end;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;

const StatChip = styled.div`
  background: #eef2ff;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 8px 10px;
  font-size: 0.82rem;
  font-weight: 800;
  color: ${COLOR.text};
`;

const MainLayout = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 2.1fr) minmax(0, 1.1fr);
  gap: 12px;
  align-items: flex-start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.06);
  display: grid;
  gap: 12px;
`;

const CardHeader = styled.header`
  display: grid;
  gap: 4px;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: ${COLOR.text};
`;

const CardSub = styled.div`
  font-size: 0.84rem;
  color: ${COLOR.muted};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 10px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${COLOR.text};
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const OptionalTag = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--app-primary-soft);
  border: 1px solid rgba(191, 219, 254, 0.8);
  font-size: 0.7rem;
  font-weight: 600;
  color: ${COLOR.muted};
`;

const Input = styled.input`
  border-radius: 10px;
  border: 1px solid rgba(209, 213, 219, 0.9);
  padding: 9px 11px;
  font: inherit;
  outline: none;
  background: #f9fafb;

  &:focus {
    border-color: ${COLOR.accent};
    box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
    background: #ffffff;
  }
`;

const Select = styled.select`
  border-radius: 10px;
  border: 1px solid rgba(209, 213, 219, 0.9);
  padding: 9px 11px;
  font: inherit;
  outline: none;
  background: #f9fafb;
  cursor: pointer;

  &:focus {
    border-color: ${COLOR.accent};
    box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
    background: #ffffff;
  }
`;

const Textarea = styled.textarea`
  border-radius: 10px;
  border: 1px solid rgba(209, 213, 219, 0.9);
  padding: 9px 11px;
  font: inherit;
  outline: none;
  background: #f9fafb;
  resize: vertical;
  min-height: 130px;

  &:focus {
    border-color: ${COLOR.accent};
    box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
    background: #ffffff;
  }
`;

const Helper = styled.div`
  font-size: 0.78rem;
  color: ${COLOR.muted};
`;

const Actions = styled.div`
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TypeChip = styled.div`
  padding: 5px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid rgba(148, 163, 184, 0.7);
  font-size: 0.78rem;
  color: ${COLOR.muted};

  strong {
    color: ${COLOR.text};
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border-radius: 999px;
  border: none;
  background: ${COLOR.primary};
  color: #ffffff;
  font-weight: 800;
  font-size: 0.86rem;
  cursor: pointer;
  min-width: 150px;
  justify-content: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SidePanel = styled.aside`
  background: ${COLOR.card};
  border-radius: 16px;
  border: 1px dashed rgba(148, 163, 184, 0.85);
  padding: 14px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  font-size: 0.84rem;
  color: ${COLOR.text};

  ul {
    margin: 6px 0 0 18px;
    padding: 0;
    color: ${COLOR.muted};
    line-height: 1.5;
  }

  li + li {
    margin-top: 4px;
  }
`;

const SideTitle = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 4px;
`;

const SideDivider = styled.div`
  height: 1px;
  background: rgba(226, 232, 240, 0.9);
  margin: 10px 0;
`;

const SideText = styled.div`
  font-size: 0.82rem;
  color: ${COLOR.muted};
  line-height: 1.5;
`;
